import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { COLLECTIONS_BY_BRAND, PRODUCTS, IMAGES } from '../mock';

const slugifyBrand = (b) =>
  b.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const ShopByVehicle = () => {
  // Brands sorted alphabetically with product counts
  const brands = useMemo(() => {
    const counts = {};
    PRODUCTS.forEach((p) => {
      const b = p.brand || 'Other';
      counts[b] = (counts[b] || 0) + 1;
    });
    return Object.keys(COLLECTIONS_BY_BRAND)
      .map((name) => ({
        name,
        modelCount: COLLECTIONS_BY_BRAND[name].length,
        productCount: PRODUCTS.filter((p) =>
          COLLECTIONS_BY_BRAND[name].some((m) => m.id === p.collectionId)
        ).length,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  return (
    <div className="bg-white text-neutral-900 min-h-screen">
      {/* Hero */}
      <section
        className="relative pt-40 pb-16 text-white"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.92), rgba(0,0,0,0.7)), url(${IMAGES.workshop})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-3">
            SHOP BY VEHICLE
          </div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tight">
            FIND PARTS FOR YOUR BUILD.
          </h1>
          <p className="text-neutral-300 mt-4 max-w-2xl">
            Pick your manufacturer below to see every chassis we outfit &mdash; from
            track-focused aero to refined OEM+ dry carbon detail.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="border-b border-neutral-100 py-4">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-sm text-neutral-500">
          <Link to="/" className="hover:text-[#FF6B00]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-neutral-900">Shop by Vehicle</span>
        </div>
      </section>

      {/* Type-forward brand list */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <ul className="divide-y divide-neutral-200">
            {brands.map((b) => (
              <li key={b.name}>
                <Link
                  to={`/shop/${slugifyBrand(b.name)}`}
                  data-testid={`shop-brand-${slugifyBrand(b.name)}`}
                  className="group flex items-baseline justify-between gap-6 py-5 transition-colors hover:bg-neutral-50 px-2 -mx-2"
                >
                  <div className="flex items-baseline gap-5 min-w-0">
                    <span className="font-display text-3xl md:text-5xl lg:text-6xl tracking-tight text-neutral-900 group-hover:text-[#FF6B00] transition-colors">
                      {b.name.toUpperCase()}
                    </span>
                    <span className="hidden sm:inline font-display tracking-[0.18em] text-xs text-neutral-400">
                      {b.modelCount} MODEL{b.modelCount !== 1 ? 'S' : ''}
                      <span className="mx-2 text-neutral-300">&middot;</span>
                      {b.productCount} PART{b.productCount !== 1 ? 'S' : ''}
                    </span>
                  </div>
                  <ArrowRight
                    className="text-neutral-300 group-hover:text-[#FF6B00] group-hover:translate-x-1 transition-all shrink-0"
                    size={28}
                  />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Wheels callout */}
      <section className="bg-neutral-50 border-t border-neutral-100 py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-2">
              FORTUNE FORGED
            </div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight">
              LOOKING FOR WHEELS INSTEAD?
            </h2>
            <p className="text-neutral-600 mt-2 max-w-md">
              12 custom Fortune Forged wheels, available monoblock or 2-piece, with custom fitments and finishes.
            </p>
          </div>
          <Link
            to="/wheels"
            data-testid="shop-cta-wheels"
            className="inline-flex items-center gap-3 bg-black hover:bg-neutral-900 text-white px-7 py-4 font-display tracking-[0.22em] text-sm font-bold transition-colors"
          >
            BROWSE WHEELS <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ShopByVehicle;
